import { useState, useEffect, useCallback } from 'react';
import vehicleService from '../services/vehicleService';
import { getApiBaseUrl } from '../utils/axios';

export const useVehicleLog = () => {
  // Estados principales
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiConnected, setApiConnected] = useState(false);
  const [filters, setFilters] = useState({
    estado: null,
    fecha_desde: null,
    fecha_hasta: null,
    placa: null,
    numero_calza: null,
    sucursal_id: null,
    // 'actividad': ordena por lo más reciente entre entrada y salida (igual
    // que la tarjeta "Actividad reciente" del dashboard, pero corregido) —
    // un vehículo que salió hace 2 minutos aparece arriba de uno que sigue
    // dentro desde hace días.
    order_by: 'actividad',
    order_dir: 'desc',
  });
  
  // Estados de filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalVehicles, setTotalVehicles] = useState(0);

  const buildImageSrc = (value, tipo) => {
    if (!value) return null;
    if (value.startsWith('data:') || value.length > 500) return value;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    const base = getApiBaseUrl();
    if (value.startsWith('/api/')) return `${base}${value}`;
    return `${base}/api/images/${tipo}/${value}`;
  };

  const processVehiclesWithImages = (vehiclesList) => {
    return vehiclesList.map(vehicle => {
      const imagenes = [];
      const srcEntrada = buildImageSrc(vehicle.imagen_entrada, 'entrada');
      if (srcEntrada) {
        imagenes.push({ id: `entrada-${vehicle.id}`, url: srcEntrada, name: 'Entrada' });
      }
      const srcSalida = buildImageSrc(vehicle.imagen_salida, 'salida');
      if (srcSalida) {
        imagenes.push({ id: `salida-${vehicle.id}`, url: srcSalida, name: 'Salida' });
      }
      return { ...vehicle, imagenes };
    });
  };

  // filtrosOverride: para usar justo después de cambiar los filtros, sin
  // esperar al siguiente render — "filters" en el cuerpo de esta función es
  // el de cuando se CREÓ este callback (useCallback), no el que se acaba de
  // fijar con setFilters() en la misma función que llama a loadVehicles.
  // Sin esto, buscar con un filtro nuevo disparaba este pedido con el
  // filtro VIEJO a la vez que el efecto disparaba otro con el nuevo — una
  // carrera donde, si el viejo (sin filtrar, más lento) respondía último,
  // pisaba el resultado ya filtrado y parecía que el filtro no hacía nada.
  const loadVehicles = useCallback(async (pageNumber = 1, showLoader = true, filtrosOverride = null, perPageOverride = null) => {
    const filtrosAUsar = filtrosOverride || filters;
    const perPageAUsar = perPageOverride || perPage;
    console.log(`📊 Cargando página ${pageNumber} de ${totalPages || '?'} (${perPageAUsar} registros/página)`);

    if (showLoader) setLoading(true);
    setError(null);

    try {
      const response = await vehicleService.obtenerBitacora({
        page: pageNumber,
        per_page: perPageAUsar,
        ...filtrosAUsar,
      });
  
      if (response.success) {
        const processedVehicles = processVehiclesWithImages(response.data);
        console.log(`✅ Página ${pageNumber}: ${processedVehicles.length} vehículos | Total: ${response.pagination.total}`);
        
        setVehicles(processedVehicles);
        setCurrentPage(pageNumber);
        setTotalVehicles(response.pagination.total);
        setTotalPages(response.pagination.total_pages);
        setApiConnected(true);
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
      
    } catch (error) {
      console.error('❌ Error cargando vehículos:', error);
      setError(error.message);
      setApiConnected(false);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [perPage, filters, totalPages]);


  const loadAllVehiclesByDate = useCallback(async (filters = {}) => {
    try {
      const response = await vehicleService.obtenerTodosVehiculos(filters);

      if (response.success) {
        const processedVehicles = processVehiclesWithImages(response.data);
        return processedVehicles;
      }

      return [];
    } catch (error) {
      console.error(' Error cargando todos los vehículos:', error);
      return [];
    }
  }, []);

  /**
   * Buscar vehículos con filtros específicos (para casos especiales)
   */
  const searchVehicles = async (searchParams) => {
    console.log('🔍 Búsqueda con filtros:', searchParams);

    // Se arma UNA vez y se le pasa directo a loadVehicles (ver su
    // comentario) — así el pedido sale con estos filtros sin depender de
    // que el estado ya se haya actualizado. setFilters igual se llama,
    // para que las próximas veces (ordenar, paginar, actualizar) sigan
    // partiendo de este mismo filtro. Mantiene order_by/order_dir ya
    // elegidos — sin el "prev =>" se perdían con cada búsqueda.
    let nuevosFiltros;
    setFilters(prev => {
      nuevosFiltros = {
        ...prev,
        estado: searchParams.estado || null,
        fecha_desde: searchParams.fecha_desde || null,
        fecha_hasta: searchParams.fecha_hasta || null,
        placa: searchParams.placa || null,
        numero_calza: searchParams.numero_calza || null,
        sucursal_id: searchParams.sucursal_id || null,
      };
      return nuevosFiltros;
    });

    setCurrentPage(1);
    await loadVehicles(1, true, nuevosFiltros);

    return { success: true };
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      loadVehicles(pageNumber, false);
    }
  };

  // Ordena en el servidor, no solo la página ya cargada — sin esto, pedir
  // "los últimos que salieron" solo reordenaba lo que ya estaba traído (y
  // ordenado por fecha de entrada), así que los vehículos realmente más
  // recientes en salir podían quedar fuera de la página visible.
  const ordenarPor = (columna) => {
    let nuevosFiltros;
    setFilters(prev => {
      const mismaColumna = prev.order_by === columna;
      const nuevaDireccion = mismaColumna && prev.order_dir === 'desc' ? 'asc' : 'desc';
      nuevosFiltros = { ...prev, order_by: columna, order_dir: nuevaDireccion };
      return nuevosFiltros;
    });
    setCurrentPage(1);
    loadVehicles(1, true, nuevosFiltros);
  };

  const changePerPage = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    loadVehicles(1, true, null, newPerPage);
  };

  /**
   * Obtener código QR de un vehículo
   */
  const getVehicleQR = async (vehicleId) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!apiConnected) {
        throw new Error('No hay conexión con el servidor');
      }

      const response = await vehicleService.obtenerQR(vehicleId);
      
      return {
        success: response.success,
        qr_code: response.qr_code,
        qr_image: response.qr_image,
        vehicle_data: response.vehicle_data,
        message: response.message,
      };
      
    } catch (error) {
      console.error('Error getting QR:', error);
      setError(error.message);
      
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar salida de vehículo
   */
  const registerExit = async (vehicleId, exitData = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!apiConnected) {
        throw new Error('No hay conexión con el servidor');
      }

      const response = await vehicleService.registrarSalida(vehicleId, exitData);

      if (response.success) {
        // Recargar lista después de la salida
        await loadVehicles();
        
        return {
          success: true,
          vehicle: response.data,
          tiempo_permanencia: response.tiempo_permanencia,
          message: response.message,
        };
      }
      
      throw new Error('Error registrando salida');
      
    } catch (error) {
      console.error('Error registering exit:', error);
      setError(error.message);
      
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Exportar datos a CSV
   */
  const exportToCSV = async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      if (!apiConnected) {
        throw new Error('No hay conexión con el servidor');
      }

      const response = await vehicleService.exportarCSV(filters);
      
      if (response.success) {
        // Crear enlace de descarga
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return {
          success: true,
          message: 'Archivo descargado exitosamente',
        };
      }
      
      throw new Error('Error en la exportación');
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError(error.message);
      
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Función para limpiar errores
   */
  const clearError = () => {
    setError(null);
  };

  // Única carga automática: la inicial, al montar. De ahí en adelante el
  // flujo es 100% explícito — cada acción del usuario (Buscar, Aplicar
  // filtros, Limpiar, ordenar columna, cambiar registros/página, paginar,
  // Actualizar) llama a loadVehicles() directamente con los filtros/página
  // que le corresponden. Antes este efecto reaccionaba a cualquier cambio
  // de "filters" (vía la identidad de loadVehicles, que dependía de
  // filters) y disparaba una SEGUNDA carga automática además de la
  // explícita de cada acción — dos pedidos por un solo clic, y la base de
  // la carrera que pisaba resultados ya filtrados.
  useEffect(() => {
    loadVehicles(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    let nuevosFiltros;
    setFilters(prev => {
      nuevosFiltros = {
        ...prev,
        estado: null,
        fecha_desde: null,
        fecha_hasta: null,
        placa: null,
        numero_calza: null,
        sucursal_id: null,
      };
      return nuevosFiltros;
    });
    setSearchTerm('');
    setCurrentPage(1);
    loadVehicles(1, true, nuevosFiltros);
  };


  return {
    vehicles,
    loading,
    error,
    apiConnected,
    currentPage,
    perPage,
    totalVehicles,
    totalPages,
    searchTerm,
    setSearchTerm,
    filters,
    searchVehicles,
    ordenarPor,
    goToPage,
    changePerPage,
    clearFilters,
    loadVehicles,
    loadAllVehiclesByDate,
  };
};